import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectGroup,
    SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import apiClient from "../../../utils/apiClient";
import apiClientChoice from "../../../utils/apiClientChoice";
import { Input } from "../../../components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import { Checkbox } from "../../../components/ui/checkbox";
import toast from "react-hot-toast";

const statusOptions = [
    { value: "active", label: "active" },
    { value: "inactive", label: "inactive" },
    { value: "deactivated", label: "deactivated" },
    { value: "dummy", label: "dummy" },
];

export function MOSLUserForm({ 
    handleAddUserIndexMultiplier, 
    onClose, 
    proxyData, 
    isOnholdLegality, 
    isLegalitiesComplete, 
    isWizard = false,
    onWizardSubmit,
    prefilledName = "",
    prefilledPhone = "",
    prefilledEmail = "",
    prefilledDob = "",
    prefilledClientId = "",
    prefilledTotpKey = "",
    prefilledPassword = "",
    prefilledApiKey = "",
    prefilledApiSecretKey = "",
    prefilledSizing = 1,
    prefilledProxyUser = "",
    prefilledProxyPassword = "",
    prefilledProxyHost = "",
    prefilledProxyPort = 0,
    prefilledProxyStaticIp = "",
    prefilledProxyToggle = false
}) {
    const [clientId, setClientId] = useState(prefilledClientId || "");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [name, setName] = useState(prefilledName || "");
    const [email, setEmail] = useState(prefilledEmail || "");
    const [phone, setPhone] = useState(prefilledPhone || "");
    const [password, setPassword] = useState(prefilledPassword || "");
    const [totpKey, setTotpKey] = useState(prefilledTotpKey || "");
    const [apiKey, setApiKey] = useState(prefilledApiKey || "");
    const [apiSecretKey, setApiSecretKey] = useState(prefilledApiSecretKey || "");
    const [dob, setDob] = useState(() => {
        if (prefilledDob && prefilledDob.includes("-")) {
            const parts = prefilledDob.split("-");
            if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
        }
        return prefilledDob || "";
    });
    const [status, setStatus] = useState("active");
    const [sizing, setSizing] = useState(prefilledSizing || 1);

    useEffect(() => {
        if (isOnholdLegality && !isLegalitiesComplete) {
            setStatus("inactive");
        }
    }, [isOnholdLegality, isLegalitiesComplete]);

    // Multipliers (not used in form for MOSL, hardcoded to default values)
    const multiplier = 1;
    const bankniftyMultiplier = 0;
    const finniftyMultiplier = 0;
    const midcpniftyMultiplier = 0;
    const sensexMultiplier = 0;

    // Proxy states
    const [proxyUser, setProxyUser] = useState(prefilledProxyUser || "");
    const [proxyPassword, setProxyPassword] = useState(prefilledProxyPassword || "");
    const [proxyHost, setProxyHost] = useState(prefilledProxyHost || "");
    const [proxyPort, setProxyPort] = useState(prefilledProxyPort || 0);
    const [proxyStaticIp, setProxyStaticIp] = useState(prefilledProxyStaticIp || "");
    const [proxyToggle, setProxyToggle] = useState(prefilledProxyToggle || false);

    // ── Sync prefilled props asynchronously ───────────────────────────────────
    useEffect(() => {
        if (prefilledClientId) setClientId(prefilledClientId);
        if (prefilledName) setName(prefilledName);
        if (prefilledEmail) setEmail(prefilledEmail);
        if (prefilledPhone) setPhone(prefilledPhone);
        if (prefilledPassword) setPassword(prefilledPassword);
        if (prefilledTotpKey) setTotpKey(prefilledTotpKey);
        if (prefilledApiKey) setApiKey(prefilledApiKey);
        if (prefilledApiSecretKey) setApiSecretKey(prefilledApiSecretKey);
        if (prefilledDob) {
            if (prefilledDob.includes("-")) {
                const parts = prefilledDob.split("-");
                if (parts.length === 3) {
                    setDob(`${parts[2]}/${parts[1]}/${parts[0]}`);
                } else {
                    setDob(prefilledDob);
                }
            } else {
                setDob(prefilledDob);
            }
        }
        if (prefilledSizing !== undefined && prefilledSizing !== null) setSizing(prefilledSizing);
        if (prefilledProxyUser) setProxyUser(prefilledProxyUser);
        if (prefilledProxyPassword) setProxyPassword(prefilledProxyPassword);
        if (prefilledProxyHost) setProxyHost(prefilledProxyHost);
        if (prefilledProxyPort !== undefined && prefilledProxyPort !== null) setProxyPort(prefilledProxyPort);
        if (prefilledProxyStaticIp) setProxyStaticIp(prefilledProxyStaticIp);
        if (prefilledProxyToggle !== undefined && prefilledProxyToggle !== null) setProxyToggle(prefilledProxyToggle);
    }, [
        prefilledClientId, prefilledName, prefilledEmail, prefilledPhone,
        prefilledPassword, prefilledTotpKey, prefilledApiKey, prefilledApiSecretKey,
        prefilledDob, prefilledSizing, prefilledProxyUser, prefilledProxyPassword,
        prefilledProxyHost, prefilledProxyPort, prefilledProxyStaticIp,
        prefilledProxyToggle
    ]);

    // Visibility states
    const [showPassword, setShowPassword] = useState(false);
    const [showTotpKey, setShowTotpKey] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [showApiSecretKey, setShowApiSecretKey] = useState(false);
    const [showProxyPassword, setShowProxyPassword] = useState(false);

    // Auto-fill proxy details if available
    useEffect(() => {
        if (proxyData) {
            setProxyHost(proxyData.proxy_host || "");
            setProxyPort(proxyData.proxy_port || 0);
            setProxyUser(proxyData.proxy_user || "");
            setProxyPassword(proxyData.proxy_password || "");
            setProxyStaticIp(proxyData.proxy_static_ip || "");
            setProxyToggle(true);
        }
    }, [proxyData]);

    // Format DOB as dd/mm/yyyy while typing
    const handleDobChange = (e) => {
        let raw = e.target.value.replace(/[^\d]/g, ""); // digits only
        if (raw.length > 8) raw = raw.slice(0, 8);
        let formatted = raw;
        if (raw.length > 4) {
            formatted = raw.slice(0, 2) + "/" + raw.slice(2, 4) + "/" + raw.slice(4);
        } else if (raw.length > 2) {
            formatted = raw.slice(0, 2) + "/" + raw.slice(2);
        }
        setDob(formatted);
    };

    const handleAddMOSLUser = () => {
        const cId = (clientId || "").trim();
        const nm = (name || "").trim();
        const em = (email || "").trim();
        const ph = (phone || "").trim();
        const pwd = (password || "").trim();
        const totp = (totpKey || "").trim();
        const key = (apiKey || "").trim();
        const sec = (apiSecretKey || "").trim();
        const d = (dob || "").trim();
        const pUser = (proxyUser || "").trim();
        const pPwd = (proxyPassword || "").trim();
        const pHost = (proxyHost || "").trim();
        const pStaticIp = (proxyStaticIp || "").trim();

        const choicePayload = {
            client_id: cId,
            broker: "MOSL",
            name: nm,
            email: em,
            phone: ph,
            account_status: status.toUpperCase(),
            sizing: parseFloat(sizing) || 0,
            password: pwd,
            api_key: key,
            api_secret_key: sec,
            totp_key: totp,
            dob: d,
            proxy_user: pUser,
            proxy_password: pPwd,
            proxy_host: pHost,
            proxy_port: parseInt(proxyPort) || 0,
            proxy_toggle: !!proxyToggle,
            proxy_static_ip: pStaticIp
        };

        const extraFields = {
            api_key: key,
            api_secret: sec,
            totp_key: totp,
            password: pwd,
            dob: d,
            status: status,
            sizing: parseFloat(sizing) || 0,
            proxy_user: pUser,
            proxy_password: pPwd,
            proxy_host: pHost,
            proxy_port: parseInt(proxyPort) || 0,
            proxy_toggle: !!proxyToggle,
            proxy_static_ip: pStaticIp
        };

        if (
            !choicePayload.client_id ||
            !choicePayload.name ||
            !choicePayload.password ||
            !choicePayload.totp_key ||
            !choicePayload.api_key ||
            !choicePayload.api_secret_key ||
            !choicePayload.dob ||
            !choicePayload.account_status ||
            sizing === "" || sizing === undefined
        ) {
            toast.error("Missing required fields !!", {
                duration: 5000,
            });
            return;
        }

        console.log("Sending MOSL payload to Choice URL:", choicePayload);

        // Wizard mode: save for approval
        if (isWizard && onWizardSubmit) {
            onWizardSubmit({
                client_id: cId,
                brokerData: { ...choicePayload, ...extraFields },
            });
            return;
        }

        let config = {
            method: "post",
            url: "https://choice.server.sypto.xyz/add/user",
            headers: {
                "Content-Type": "application/json",
            },
            data: JSON.stringify(choicePayload),
        };

        // We make request directly to choice endpoint
        setIsSubmitting(true);
        apiClientChoice
            .request(config)
            .then((response) => {
                console.log("Choice server response:", response.data);
                toast.success("New MOSL user added to Choice successfully!", {
                    duration: 5000,
                });
                
                // Trigger onboarding save in local master_user_table
                if (handleAddUserIndexMultiplier) {
                    handleAddUserIndexMultiplier(
                        clientId.trim(),
                        name.trim(),
                        parseFloat(multiplier),
                        parseInt(bankniftyMultiplier),
                        parseInt(finniftyMultiplier),
                        parseInt(midcpniftyMultiplier),
                        parseInt(sensexMultiplier),
                        extraFields
                    );
                }
            })
            .catch((error) => {
                console.error("Failed to add new MOSL user to Choice", error);
                toast.error("Failed to add user to Choice");
                onClose(false);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className={isWizard ? "font-inter space-y-4" : "font-inter space-y-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4"}>
            <div className="grid grid-cols-4 gap-6 py-4">
                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Client Id <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Email
                    </Label>
                    <Input
                        className="text-white"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Phone
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        TOTP Key <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showTotpKey ? "text" : "password"}
                            value={totpKey}
                            onChange={(e) => setTotpKey(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowTotpKey(!showTotpKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showTotpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        API Key <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showApiKey ? "text" : "password"}
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        API Secret Key <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showApiSecretKey ? "text" : "password"}
                            value={apiSecretKey}
                            onChange={(e) => setApiSecretKey(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiSecretKey(!showApiSecretKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showApiSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        DOB (2FA) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        placeholder="dd/mm/yyyy"
                        maxLength={10}
                        value={dob}
                        onChange={handleDobChange}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Status <span className="text-red-500">*</span>
                    </Label>
                    <Select value={status} onValueChange={(val) => setStatus(val)} disabled={isOnholdLegality && !isLegalitiesComplete}>
                        <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                            <SelectValue placeholder="Select Status" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-700">
                            <SelectGroup>
                                {statusOptions.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Sizing <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="number"
                        step="0.01"
                        value={sizing}
                        onChange={(e) => setSizing(e.target.value)}
                    />
                </div>


                {/* Proxy Fields */}
                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy User</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={proxyUser}
                        onChange={(e) => setProxyUser(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Password</Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showProxyPassword ? "text" : "password"}
                            value={proxyPassword}
                            onChange={(e) => setProxyPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowProxyPassword(!showProxyPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showProxyPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Host</Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={proxyHost}
                        onChange={(e) => setProxyHost(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Port</Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={proxyPort}
                        onChange={(e) => setProxyPort(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Proxy Static IP</Label>
                    <Input
                        className="text-white"
                        type="text"
                        placeholder="e.g. 2402:1f00:8300:989::1"
                        value={proxyStaticIp}
                        onChange={(e) => setProxyStaticIp(e.target.value)}
                    />
                </div>

                <div className="flex items-center space-x-2 h-full pt-8">
                    <Checkbox
                        id="mosl-proxy-toggle"
                        checked={proxyToggle}
                        onCheckedChange={(checked) => setProxyToggle(checked)}
                    />
                    <Label htmlFor="mosl-proxy-toggle" className="text-white cursor-pointer">
                        Proxy Toggle
                    </Label>
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <Button
                    disabled={
                        isSubmitting ||
                        !clientId ||
                        !name ||
                        !password ||
                        !totpKey ||
                        !apiKey ||
                        !apiSecretKey ||
                        !dob ||
                        !status ||
                        sizing === "" || sizing === undefined
                    }
                    type="submit"
                    className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                    onClick={handleAddMOSLUser}
                >
                    {isSubmitting ? "Adding..." : isWizard ? "Submit for Approval" : "Add MOSL User"}
                </Button>
            </div>
        </div>
    );
}
